/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { TrackingConsent } from '@/types/tracking-consent';
import { resourceRoutes } from '@/route-paths';
import { ONETRUST_CONSENT_CHANGED_EVENT, mapActiveGroupsToTrackingConsent, readActiveGroups } from './mapping';

/** CSS custom property `<OneTrustBridge />` writes on `document.documentElement`. Read by `src/theme/base.css` and the mobile CTA classNames on cart/checkout/live-person to lift fixed-bottom elements above the OT banner. */
const OT_BANNER_HEIGHT_CSS_VAR = '--ot-banner-height';
/** OneTrust's own banner container id (from the vendor `otSDKStub.js`). Fixed by OneTrust — do not rename. */
const OT_BANNER_ELEMENT_ID = 'onetrust-banner-sdk';

/**
 * OneTrust CMP → Storefront Next consent bridge (INT-60 PR 3).
 *
 * When `appConfig.onetrust.enabled === true`, this client component subscribes
 * to the same-origin `ashley:onetrust:consent-changed` `CustomEvent` fired by
 * the inline `OptanonWrapper()` in `src/root.tsx`. On every consent decision
 * it reads `window.OnetrustActiveGroups`, maps the OneTrust group IDs against
 * `appConfig.onetrust.categoryMap`, and — when `bridgeDwDnt` is true — dispatches
 * a `TrackingConsent.Accepted | Declined` to `/action/update-tracking-consent`
 * so the SLAS token + `dw_dnt` cookie stay aligned with the OT decision.
 *
 * Fire-and-forget: the fetcher submit is not awaited and never blocks banner
 * interaction. Idempotent: the last-sent decision is memoised, so a repeated
 * identical `OnetrustActiveGroups` payload (which OT can emit multiple times
 * — e.g. on initial-load geo classification followed by an implicit consent
 * event) does not accumulate duplicate SLAS refresh calls.
 *
 * Renders nothing. Delegated by `src/root.tsx` — do not mount this component
 * outside the root; its lifecycle must match the app shell so the `window`
 * subscription lives for the entire session.
 *
 * PR 4 will additionally rewrite `buildConsentPreferences()` to read
 * `window.OnetrustActiveGroups` directly so engagement adapters gate per
 * category. This component is the shim that keeps the binary `dw_dnt` path
 * (used by SFCC server-side Active Data + Einstein) in sync with the CMP for
 * as long as the two systems co-exist. See INT-60 §"OneTrust ⇄ dw_dnt bridging".
 */
export function OneTrustBridge() {
    const config = useConfig();
    const fetcher = useFetcher();
    const lastSentRef = useRef<TrackingConsent | null>(null);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    const onetrust = config.onetrust;
    const enabled = onetrust?.enabled === true;
    const bridgeDwDnt = onetrust?.bridgeDwDnt !== false;
    const analyticsCategory = onetrust?.categoryMap?.analytics ?? 'C0002';
    const advertisingCategory = onetrust?.categoryMap?.advertising ?? 'C0004';

    useEffect(() => {
        if (!enabled || !bridgeDwDnt || typeof window === 'undefined') {
            return;
        }

        const handleConsentChanged = () => {
            const consent = mapActiveGroupsToTrackingConsent(readActiveGroups(), {
                analytics: analyticsCategory,
                advertising: advertisingCategory,
            });
            if (consent === null || lastSentRef.current === consent) {
                return;
            }
            lastSentRef.current = consent;
            void fetcherRef.current.submit(
                { trackingConsent: consent },
                { method: 'POST', action: resourceRoutes.updateTrackingConsent }
            );
        };

        window.addEventListener(ONETRUST_CONSENT_CHANGED_EVENT, handleConsentChanged);
        // Handle the case where OneTrust has already dispatched its first
        // OptanonWrapper() before this component mounts (script may finish
        // loading during hydration, especially on fast connections). The
        // inline OptanonWrapper() sets a boolean flag we can check.
        if (typeof window.__ashleyOnetrustReady === 'boolean' && window.__ashleyOnetrustReady) {
            handleConsentChanged();
        }
        return () => {
            window.removeEventListener(ONETRUST_CONSENT_CHANGED_EVENT, handleConsentChanged);
        };
    }, [enabled, bridgeDwDnt, analyticsCategory, advertisingCategory]);

    useOneTrustBannerOffset(enabled);

    return null;
}

/**
 * Publish OneTrust's rendered banner height as a CSS custom property on
 * `document.documentElement` so fixed-position UI (cart / checkout / chat
 * launcher mobile CTAs) can lift above it. SSF-603.
 *
 * The vendor script from `cookielaw.org` paints its own DOM at a z-index
 * near `INT_MAX`, so any fixed element sitting at `bottom: 0` on mobile
 * gets covered until the shopper dismisses the banner — a real conversion
 * risk on cart's "Proceed to Checkout" and checkout's "Place Order". This
 * hook watches the banner element's presence and size, writes its rendered
 * height into `--ot-banner-height`, and clears the var when the banner is
 * dismissed / removed. The value is consumed by:
 *
 * - `src/theme/base.css` — `body { padding-bottom: var(--ot-banner-height,0px) }`
 *   so page content scrolls fully past the banner.
 * - Fixed-bottom CTAs — `bottom-[var(--ot-banner-height,0px)]` so they rest
 *   *on top of* the banner rather than *behind* it.
 *
 * All work is scoped to the client (no `window` = no-op) AND gated on the
 * OneTrust `enabled` flag, so environments running the OOTB
 * `TrackingConsentBanner` see zero behaviour change. When the banner element
 * isn't in the DOM yet, a MutationObserver waits for it; when it appears, a
 * ResizeObserver tracks height changes (e.g. long policy text wrapping to
 * a taller banner on narrow viewports).
 */
function useOneTrustBannerOffset(enabled: boolean): void {
    useEffect(() => {
        if (!enabled || typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        const root = document.documentElement;
        let resizeObserver: ResizeObserver | null = null;
        let trackedBanner: HTMLElement | null = null;

        const clearBannerOffset = () => {
            root.style.removeProperty(OT_BANNER_HEIGHT_CSS_VAR);
        };

        const writeBannerOffset = (banner: HTMLElement) => {
            // `offsetHeight` returns 0 when the banner is display:none — treat
            // that as "not visible" and clear the var so CTAs sit at bottom-0.
            const h = banner.offsetHeight;
            if (h > 0) {
                root.style.setProperty(OT_BANNER_HEIGHT_CSS_VAR, `${h}px`);
            } else {
                clearBannerOffset();
            }
        };

        const attach = (banner: HTMLElement) => {
            if (trackedBanner === banner) return;
            resizeObserver?.disconnect();
            trackedBanner = banner;
            writeBannerOffset(banner);
            if (typeof ResizeObserver !== 'undefined') {
                resizeObserver = new ResizeObserver(() => writeBannerOffset(banner));
                resizeObserver.observe(banner);
            }
        };

        const detach = () => {
            resizeObserver?.disconnect();
            resizeObserver = null;
            trackedBanner = null;
            clearBannerOffset();
        };

        // If OT already painted the banner before this effect runs, wire up now.
        const existing = document.getElementById(OT_BANNER_ELEMENT_ID);
        if (existing instanceof HTMLElement) {
            attach(existing);
        }

        // Watch `<body>` for the banner appearing / disappearing. Scoped to
        // childList+subtree so we catch OT's late insertion without paying for
        // attribute-mutation notifications the vendor emits heavily on every
        // consent decision.
        const mutationObserver = new MutationObserver(() => {
            const current = document.getElementById(OT_BANNER_ELEMENT_ID);
            if (current instanceof HTMLElement) {
                attach(current);
            } else if (trackedBanner) {
                detach();
            }
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        return () => {
            mutationObserver.disconnect();
            detach();
        };
    }, [enabled]);
}

/**
 * Ambient window augmentation for the OneTrust globals this bridge reads.
 * These are set by `otSDKStub.js` (external vendor script) — declared here
 * so consumers get typing without leaking to library authors that would need
 * to `declare global` in every file.
 */
declare global {
    interface Window {
        /** Comma-separated OneTrust active group IDs (e.g. `",C0001,C0002,"`). */
        OnetrustActiveGroups?: string;
        /** Set to `true` by the inline `OptanonWrapper()` after OT's first invocation. */
        __ashleyOnetrustReady?: boolean;
    }
}
